import { CommonModule, DOCUMENT } from '@angular/common'
import { AfterViewInit, ChangeDetectionStrategy, Component, HostBinding, Input, OnDestroy, Renderer2, inject } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'

/**
 * @public
 */
export type MapElement = NgElement & WithProperties<MapComponent>

/**
 * @public
 */
@Component({
  selector: 'gpei-map',
  templateUrl: './map.component.html',
  styleUrls: [ './map.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ CommonModule ]
})
export class MapComponent implements AfterViewInit, OnDestroy {

  static readonly DEFAULT_ARCGIS_CSS = 'https://js.arcgis.com/4.28/esri/themes/light/main.css'
  static readonly DEFAULT_ARCGIS_JS = 'https://js.arcgis.com/4.28/'

  private readonly _dom = inject(DOCUMENT)
  private readonly _renderer2 = inject(Renderer2)

  private _arcgisLinkElement: HTMLLinkElement
  private _arcgisScriptElement: HTMLScriptElement
  private _mapLinkElement: HTMLLinkElement
  private _mapScriptElement: HTMLScriptElement
  private _arcGISScriptLoadUnlisten: () => void
  private _dataString: unknown

  private _arcgisCss = MapComponent.DEFAULT_ARCGIS_CSS
  private _arcgisJs = MapComponent.DEFAULT_ARCGIS_JS
  private _notModule = false

  @Input()
  @HostBinding()
  mapCss: string

  @Input()
  @HostBinding()
  mapJs: string

  @Input()
  @HostBinding()
  height: string

  @Input()
  @HostBinding()
  width: string

  @Input()
  @HostBinding()
  mapTitle: string

  @Input()
  @HostBinding()
  mapServices: string

  @Input()
  @HostBinding()
  workflow: string

  ngAfterViewInit(): void {
    this.initializeAssets()
  }

  /**
   * Lifecycle hook for when the component is destroyed.
   *
   * Tear down the HTMLLinkElements and HTMLScriptElements for the ARCGIS map.
   */
  ngOnDestroy(): void {
    this.removeAssets()
  }

  initializeAssets(): void {
    // Insert the style sheets
    this._arcgisLinkElement = this._renderer2.createElement('link')
    this._renderer2.setAttribute(this._arcgisLinkElement, 'rel', 'stylesheet')
    this._renderer2.setAttribute(this._arcgisLinkElement, 'href', this.arcgisCss ?? MapComponent.DEFAULT_ARCGIS_CSS)
    this._renderer2.appendChild(this._dom.head, this._arcgisLinkElement)

    if (this.mapCss) {
      this._mapLinkElement = this._renderer2.createElement('link')
      this._renderer2.setAttribute(this._mapLinkElement, 'rel', 'stylesheet')
      this._renderer2.setAttribute(this._mapLinkElement, 'href', this.mapCss)
      this._renderer2.appendChild(this._dom.head, this._mapLinkElement)
    }

    this._arcgisScriptElement = this._renderer2.createElement('script')
    this._renderer2.setAttribute(this._arcgisScriptElement, 'type', 'text/javascript')
    this._renderer2.setAttribute(this._arcgisScriptElement, 'src', this.arcgisJs ?? MapComponent.DEFAULT_ARCGIS_JS)
    this._renderer2.appendChild(this._dom.body, this._arcgisScriptElement)

    this._arcGISScriptLoadUnlisten = this._renderer2.listen(this._arcgisScriptElement, 'load', () => {
      if (this.mapJs) {
        this._mapScriptElement = this._renderer2.createElement('script')
        let mapJsType = this._notModule ? 'text/javascript' : 'module'
        this._renderer2.setAttribute(this._mapScriptElement, 'type', mapJsType)
        this._renderer2.setAttribute(this._mapScriptElement, 'src', this.mapJs)
        this._renderer2.appendChild(this._dom.body, this._mapScriptElement)
      }
    })
  }

  removeAssets(): void {
    this._arcGISScriptLoadUnlisten()
    this._renderer2.removeChild(this._arcgisLinkElement.parentElement, this._arcgisLinkElement)
    this._renderer2.removeChild(this._arcgisScriptElement.parentElement, this._arcgisScriptElement)
    this._renderer2.removeChild(this._mapLinkElement.parentElement, this._mapLinkElement)
    this._renderer2.removeChild(this._mapScriptElement.parentElement, this._mapScriptElement)
  }

  /**
   * Determine whether or not the type of the map script is `"module"` or `"text/javascript"`
   *
   * @todo {@link https://jira.gpei.ca/browse/API-4054 API-4054}
   */
  @Input()
  @HostBinding()
  set notModule(value: boolean | string) {
    let pendingValue = typeof value === 'string' ?
      value.toLowerCase() === 'true' :
      value

    this._notModule = pendingValue

    if (this._mapScriptElement) {
      let mapJsType = this._notModule ? 'text/javascript' : 'module'
      this._renderer2.setAttribute(this._mapScriptElement, 'type', mapJsType)
    }
  }

  get notModule(): boolean {
    return this._notModule
  }

  @Input()
  @HostBinding()
  set arcgisCss(value: string) {
    this._arcgisCss = value
  }

  get arcgisCss(): string {
    return this._arcgisCss ?
      this._arcgisCss :
      MapComponent.DEFAULT_ARCGIS_CSS
  }

  @Input()
  @HostBinding()
  set arcgisJs(value: string) {
    this._arcgisJs = value
  }

  get arcgisJs(): string {
    return this._arcgisJs ?
      this._arcgisJs :
      MapComponent.DEFAULT_ARCGIS_JS
  }

  @Input()
  @HostBinding()
  set dataString(value : unknown) {
    if (typeof value === 'string' && value.trim() !== '') {
      this._dataString = JSON.parse(value)
    }

    else {
      this._dataString = value
    }
  }

  get dataString(): unknown {
    return this._dataString
  }

}

/**
 * @public
 */
export const registerMapElement = registerCustomElementFactory(MapComponent)
